// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Alert, Badge, Button, Card, Group, Select, Stack, Text, Title } from '@mantine/core';
import { IconPlayerPlay, IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CurrencyDisplay } from '../../components/CurrencyDisplay';
import { usePayrollRuns } from '../../hooks/usePayroll';
import type { PayrollRun } from '../../types/payroll';

/**
 * PayrollListPage — Action-first payroll management.
 *
 * KEY DIFFERENCE FROM TIMETREX:
 * TimeTrex shows a flat list of payroll runs. To fix an error you must:
 * 1. Find the payroll run
 * 2. Open it
 * 3. Find the problematic pay stub
 * 4. Check its "printed" status
 * 5. Delete it (only if not "posted")
 * 6. Re-run payroll
 *
 * Our design:
 * - Errors surface automatically at the top ("Needs Attention")
 * - One-click "Void & Rerun" handles the entire unwind
 * - Dry-run preview catches problems BEFORE they become errors
 * - Status is clear: no "printed but not actually printed" confusion
 */
export default function PayrollListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data, loading } = usePayrollRuns({
    page,
    perPage: pageSize,
    status: statusFilter || undefined,
  });

  // Surface runs that need attention (failed, have exceptions, or stuck)
  const needsAttention = (data ?? []).filter(
    (run) => run.status === 'failed' || run.status === 'pending_approval',
  );

  const columns: Column<PayrollRun>[] = [
    {
      key: 'payPeriod',
      label: 'Pay Period',
      sortable: true,
      render: (run) => {
        const start = new Date(run.payPeriod.startDate).toLocaleDateString();
        const end = new Date(run.payPeriod.endDate).toLocaleDateString();
        return `${start} - ${end}`;
      },
    },
    {
      key: 'runType',
      label: 'Type',
      width: 100,
      render: (run) => run.runType.charAt(0).toUpperCase() + run.runType.slice(1),
    },
    {
      key: 'status',
      label: 'Status',
      width: 140,
      render: (run) => <StatusBadge status={run.status} />,
    },
    {
      key: 'employeeCount',
      label: 'Employees',
      width: 90,
      render: (run) => String(run.employeeCount),
    },
    {
      key: 'totalGross',
      label: 'Gross Pay',
      render: (run) => <CurrencyDisplay amount={run.totalGross} size="sm" />,
    },
    {
      key: 'totalNet',
      label: 'Net Pay',
      render: (run) => <CurrencyDisplay amount={run.totalNet} size="sm" />,
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      width: 120,
      render: (run) => new Date(run.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Payroll Runs"
        subtitle={data ? `${data.length} runs` : undefined}
        actions={
          <Button
            icon={<IconPlayerPlay size={16} />}
            onClick={() => navigate('/admin/workforce/payroll/run')}
          >
            Run Payroll
          </Button>
        }
      />

      {/* ── Needs Attention ─────────────────────────────────────────
           Surfaces problems automatically instead of making users
           hunt through lists. Each item has a one-click resolution.
      */}
      {needsAttention.length > 0 && (
        <Card withBorder p="md" bg="var(--mantine-color-red-0)">
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
              <Title order={5}>Needs Attention</Title>
            </Group>
            <Badge color="red" variant="filled" size="sm">
              {needsAttention.length}
            </Badge>
          </Group>
          <Stack gap="xs">
            {needsAttention.map((run) => (
              <Alert key={run.id} variant="light" color={run.status === 'failed' ? 'red' : 'orange'} p="xs">
                <Group justify="space-between" ta="center">
                  <div>
                    <Text size="sm" fw={500}>
                      {new Date(run.payPeriod.startDate).toLocaleDateString()} - {new Date(run.payPeriod.endDate).toLocaleDateString()}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {run.status === 'failed'
                        ? 'Payroll run failed. Click to review and rerun.'
                        : 'Awaiting approval. Review dry-run results.'}
                    </Text>
                  </div>
                  <Button
                    size="xs"
                    variant="filled"
                    color={run.status === 'failed' ? 'red' : 'orange'}
                    icon={<IconArrowRight size={14} />}
                    onClick={() => navigate(`/admin/workforce/payroll/run/${run.id}`)}
                  >
                    {run.status === 'failed' ? 'Fix & Rerun' : 'Review'}
                  </Button>
                </Group>
              </Alert>
            ))}
          </Stack>
        </Card>
      )}

      <Group gap="sm">
        <Select
          placeholder="Status"
          clearable
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          data={[
            { value: 'draft', label: 'Draft' },
            { value: 'preview', label: 'Preview (Dry-Run)' },
            { value: 'pending_approval', label: 'Pending Approval' },
            { value: 'approved', label: 'Approved' },
            { value: 'processing', label: 'Processing' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
            { value: 'voided', label: 'Voided' },
          ]}
          w={200}
        />
      </Group>

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={(data ?? []) as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total: data?.length ?? 0,
          onPageChange: setPage,
        }}
        onRowClick={(run) => navigate(`/admin/workforce/payroll/run/${(run as unknown as PayrollRun).id}`)}
        getRowKey={(run) => (run as unknown as PayrollRun).id}
        emptyMessage="No payroll runs found. Click 'Run Payroll' to start a new dry-run."
      />
    </Stack>
  );
}
